import { useEffect, useMemo, useRef } from 'react';
import { Dialog, DialogContent, DialogTitle, DialogDescription } from './ui/dialog';
import * as VisuallyHidden from '@radix-ui/react-visually-hidden';
import { Button } from './ui/button';
import { motion, AnimatePresence } from 'framer-motion';
import { Trophy, Sparkles, X } from 'lucide-react';

/**
 * Plays a celebratory chime using the Web Audio API. No external assets
 * required — synthesised on-the-fly so it works offline and doesn't bloat
 * the bundle. Plays a 3-note ascending arpeggio (C-E-G) with a soft attack
 * and a bell-like decay.
 */
const playCelebrationSound = () => {
  try {
    const AudioCtx = window.AudioContext || window.webkitAudioContext;
    if (!AudioCtx) return;
    const ctx = new AudioCtx();
    // Most browsers start the context in 'suspended' state until a user
    // gesture. The modal only opens in response to a Log click, so a resume
    // here is safe.
    if (ctx.state === 'suspended') ctx.resume();

    const notes = [523.25, 659.25, 783.99, 1046.5]; // C5, E5, G5, C6
    const now = ctx.currentTime;

    notes.forEach((freq, i) => {
      const start = now + i * 0.12;
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = 'triangle';
      osc.frequency.setValueAtTime(freq, start);

      // Quick attack, gentle decay — bell-ish
      gain.gain.setValueAtTime(0, start);
      gain.gain.linearRampToValueAtTime(0.25, start + 0.02);
      gain.gain.exponentialRampToValueAtTime(0.001, start + 0.6);

      osc.connect(gain).connect(ctx.destination);
      osc.start(start);
      osc.stop(start + 0.65);
    });

    // Close the context after sounds finish to free resources
    setTimeout(() => ctx.close().catch(() => {}), 1200);
  } catch {
    // Audio is best-effort; silently ignore on unsupported browsers.
  }
};

/**
 * Pure-CSS confetti burst that doesn't require an extra dependency. Renders 40
 * colored squares that animate outward from the center on mount.
 */
const ConfettiBurst = () => {
  const pieces = useMemo(() => (
    Array.from({ length: 40 }, (_, i) => {
      const angle = (Math.PI * 2 * i) / 40 + Math.random() * 0.3;
      const distance = 180 + Math.random() * 140;
      const colors = ['#FFD700', '#FF6B6B', '#4ECDC4', '#95E1D3', '#F38181', '#AA96DA', '#FCBAD3'];
      return {
        id: `confetti-${i}-${Math.random().toString(36).slice(2, 8)}`,
        x: Math.cos(angle) * distance,
        y: Math.sin(angle) * distance,
        rotation: Math.random() * 540,
        color: colors[i % colors.length],
        delay: Math.random() * 0.2,
        size: 6 + Math.random() * 8,
      };
    })
  ), []);

  return (
    <div className="pointer-events-none absolute inset-0 overflow-hidden" aria-hidden="true">
      <div className="absolute left-1/2 top-1/2">
        {pieces.map((p) => (
          <motion.div
            key={p.id}
            initial={{ x: 0, y: 0, opacity: 1, rotate: 0, scale: 0 }}
            animate={{ x: p.x, y: p.y, opacity: 0, rotate: p.rotation, scale: 1 }}
            transition={{ duration: 1.4, delay: p.delay, ease: 'easeOut' }}
            style={{
              position: 'absolute',
              width: p.size,
              height: p.size,
              backgroundColor: p.color,
              borderRadius: 2,
            }}
          />
        ))}
      </div>
    </div>
  );
};

/**
 * Pops a confetti + trophy modal whenever the backend reports `new_milestones`
 * after a challenge-progress log. If multiple milestones unlocked in one log
 * (e.g. user logged a big batch), the parent should call this once per
 * milestone OR pass the list and we'll show the highest-percentage one.
 */
export const MilestoneCelebrationModal = ({ isOpen, milestone, onClose }) => {
  // Keep `onClose` in a ref so the open-effect below only fires on a real
  // false→true transition of `isOpen`. Without this, parent re-renders (which
  // produce a fresh inline onClose function) would re-trigger the effect and
  // we'd play the fanfare/haptics multiple times per unlock.
  const onCloseRef = useRef(onClose);
  useEffect(() => { onCloseRef.current = onClose; }, [onClose]);

  // Play the fanfare + buzz haptics when the modal opens, then auto-dismiss
  // after 6s (still respect manual dismiss).
  useEffect(() => {
    if (!isOpen) return;
    playCelebrationSound();
    // Mobile haptics — a short triple pulse mirrors the 3-note chime.
    if (typeof navigator !== 'undefined' && typeof navigator.vibrate === 'function') {
      navigator.vibrate([60, 40, 60, 40, 100]);
    }
    const t = setTimeout(() => onCloseRef.current?.(), 6000);
    return () => clearTimeout(t);
  }, [isOpen]);

  if (!milestone) return null;

  return (
    <Dialog open={isOpen} onOpenChange={(v) => !v && onClose?.()}>
      <DialogContent
        className="max-w-md bg-black border-2 border-primary overflow-hidden p-0"
        data-testid="milestone-celebration-modal"
      >
        <VisuallyHidden.Root asChild>
          <DialogTitle>Milestone Unlocked: {milestone.name}</DialogTitle>
        </VisuallyHidden.Root>
        <VisuallyHidden.Root asChild>
          <DialogDescription>
            {milestone.description || `${milestone.percentage}% complete`}
          </DialogDescription>
        </VisuallyHidden.Root>
        <button
          onClick={onClose}
          className="absolute top-3 right-3 z-20 w-9 h-9 rounded-full bg-black/60 hover:bg-black/80 flex items-center justify-center"
          aria-label="close"
          data-testid="milestone-celebration-close"
        >
          <X className="h-4 w-4 text-white" />
        </button>

        <div className="relative bg-gradient-to-br from-yellow-600/40 via-primary/10 to-black py-12 px-8 text-center">
          <ConfettiBurst />

          <AnimatePresence>
            <motion.div
              key="trophy"
              initial={{ scale: 0, rotate: -20 }}
              animate={{ scale: 1, rotate: 0 }}
              transition={{ type: 'spring', stiffness: 200, damping: 12 }}
              className="relative inline-flex items-center justify-center mb-6"
            >
              <div className="absolute inset-0 bg-yellow-400 blur-2xl opacity-50" />
              <div className="relative h-24 w-24 rounded-full bg-gradient-to-br from-yellow-400 to-yellow-600 flex items-center justify-center shadow-2xl">
                <Trophy className="h-12 w-12 text-black" />
              </div>
            </motion.div>

            <motion.div
              key="label"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.25, duration: 0.5 }}
              className="relative"
            >
              <div className="flex items-center justify-center gap-2 font-heading text-[11px] uppercase tracking-[0.4em] text-primary mb-3">
                <Sparkles className="h-3 w-3" /> Milestone Unlocked <Sparkles className="h-3 w-3" />
              </div>
              <h2 className="font-heading text-3xl text-white uppercase tracking-wider mb-2" data-testid="milestone-celebration-name">
                {milestone.name}
              </h2>
              {milestone.description && (
                <p className="text-muted-foreground text-sm mb-2">{milestone.description}</p>
              )}
              <p className="text-yellow-400 font-heading text-lg mt-1">
                {milestone.percentage}% Complete
              </p>
            </motion.div>

            <motion.div
              key="cta"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.7 }}
              className="relative mt-6"
            >
              <Button
                onClick={onClose}
                className="bg-primary text-black hover:bg-primary/90 font-heading tracking-wider uppercase px-8"
                data-testid="milestone-celebration-dismiss"
              >
                Keep Crushing
              </Button>
            </motion.div>
          </AnimatePresence>
        </div>
      </DialogContent>
    </Dialog>
  );
};
